import { type CookieOptions, createBrowserClient } from "@supabase/ssr";
import { PARTNER_COOKIE_NAME } from "./partner-cookie";
import { applyRememberMePersistence } from "./cookie-persistence";

// @supabase/ssr's browser client writes the auth cookie with its
// DEFAULT_COOKIE_OPTIONS (maxAge = 400 days) every time it hydrates or refreshes
// the session client-side — with no way to override maxAge (cookies.js forces it
// back to the default). That silently re-persists a session-only cookie the
// server wrote for an unchecked "remember me", so it survives browser close.
//
// To honor the choice we supply our own cookie handlers that mirror the library's
// default document.cookie behavior but run each write through
// applyRememberMePersistence, stripping maxAge/expires when not remembered. The
// library still handles chunking/base64 above this layer — these functions only
// do raw name=value cookie I/O.

function tryDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function readAllCookies(): { name: string; value: string }[] {
  if (typeof document === "undefined") return [];
  return document.cookie
    .split(";")
    .map((pair) => pair.trim())
    .filter(Boolean)
    .map((pair) => {
      const eq = pair.indexOf("=");
      const name = eq === -1 ? pair : pair.slice(0, eq);
      const value = eq === -1 ? "" : pair.slice(eq + 1);
      return { name: tryDecode(name.trim()), value: tryDecode(value.trim()) };
    });
}

function serializeCookie(
  name: string,
  value: string,
  options: CookieOptions | undefined
): string {
  const o = options ?? {};
  let str = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
  if (o.maxAge != null) str += `; Max-Age=${Math.floor(o.maxAge)}`;
  if (o.expires) str += `; Expires=${new Date(o.expires).toUTCString()}`;
  if (o.domain) str += `; Domain=${o.domain}`;
  if (o.path) str += `; Path=${o.path}`;
  if (o.sameSite === true) str += `; SameSite=Strict`;
  else if (typeof o.sameSite === "string" && o.sameSite)
    str += `; SameSite=${o.sameSite.charAt(0).toUpperCase()}${o.sameSite.slice(1)}`;
  if (o.secure) str += `; Secure`;
  // httpOnly is intentionally not emitted: browsers ignore it from document.cookie,
  // and the Supabase auth cookie is not httpOnly (the browser client must read it).
  return str;
}

function isRememberedFromDocument(): boolean {
  const rememberMe = readAllCookies().find((c) => c.name === "remember_me")?.value;
  return rememberMe === undefined ? true : rememberMe === "1";
}

export function createClient() {
  // Create a supabase client on the browser with project's credentials
  return createBrowserClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
    {
      cookies: {
        getAll() {
          return readAllCookies();
        },
        setAll(
          cookiesToSet: { name: string; value: string; options?: CookieOptions }[]
        ) {
          if (typeof document === "undefined") return;
          const isRemembered = isRememberedFromDocument();
          cookiesToSet.forEach(({ name, value, options }) => {
            const finalOptions = applyRememberMePersistence(name, value, options, isRemembered);
            document.cookie = serializeCookie(name, value, finalOptions);
          });
        },
      },
    }
  );
}

// Browser client scoped to the partner-portal cookie namespace so its session
// is isolated from the dashboard session (see partner-cookie.ts). Intentionally
// left on Supabase's default persistence — the partner portal has no "remember
// me" and is out of scope for that feature.
export function createPartnerBrowserClient() {
  return createBrowserClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
    {
      cookieOptions: { name: PARTNER_COOKIE_NAME },
    }
  );
}
